import { getCachedData } from "./offlineDb"

export const isTemporaryEntityId = id => /^(offline|pending_)/.test(String(id || ""))

export const canonicalEntityId = (state, type, id) => {
  const resolved = state?.offline?.resolvedIds || {}
  const seen = new Set()
  while (resolved[id] && !seen.has(id)) {
    seen.add(id)
    id = resolved[id]
  }
  const field = { file: "files", work: "works", observation: "observations" }[type]
  const records = [
    ...(state?.[field]?.[field] || []),
    ...(state?.auditReports?.currentReportData?.[field] || []),
    ...Object.values(state?.auditReports?.allReportsData || {}).flatMap((report) => report?.[field] || []),
  ]
  if (type === "report") records.push(
    state?.auditReports?.currentReport,
    ...(state?.auditReports?.recentReports || []),
    ...(state?.auditReports?.siteReports || []),
    ...Object.values(state?.auditReports?.allReportsData || {}).map(entry => entry?.report),
  )
  if (type === "site") records.push(state?.siteData?.currentSiteData, ...(state?.siteData?.siteData || []))
  const aliases = [id, String(id || "").replace(/^offline-(report|site)-/, "")]
  const candidates = records.filter(Boolean).filter((item) => [item._id, item.id, item._clientId,
    ...(type === "file" && item._clientId ? [`pending_${item._clientId}`] : [])].some(value => value && aliases.includes(value)))
  const record = (type === "report" || type === "site")
    ? candidates.find(item => !isTemporaryEntityId(item._id || item.id)) || candidates[0]
    : candidates[0]
  return record?._id || record?.id || id
}

export const resolveEntityId = async (state, type, id) => {
  const canonical = canonicalEntityId(state, type, id)
  if (canonical !== id || !/^(pending_|offline)/.test(String(id))) return canonical
  const saved = await getCachedData("sync:resolved-ids")
  return canonicalEntityId({ ...state, offline: { ...state?.offline, resolvedIds: saved || {} } }, type, id)
}

// Read children locally while a report/site creation is still queued.
export const readLocalReportEntities = async (state, field, reportId, siteId, templateId) => {
  let record = state?.auditReports?.allReportsData?.[reportId];
  if (!record) {
    const cached = await getCachedData("audit-reports:all-with-data");
    record = (Array.isArray(cached) ? cached : []).find(entry => entry?.report?._id === reportId);
  }
  return (record?.[field] || state?.[field]?.[field] || []).filter(item =>
    (!reportId || canonicalEntityId(state, "report", item.reportId) === reportId) &&
    (!siteId || !item.siteId || canonicalEntityId(state, "site", item.siteId) === siteId) &&
    (!templateId || !item.templateId || item.templateId === templateId));
}

// Report reads share one ownership rule: local data wins while sync is active,
// and a response from before that sync run must not replace its final result.
export const createReportReadGuard = (getState, reportId) => {
  const generation = getState()?.offline?.syncGeneration
  return () => {
    const state = getState()
    return state?.offline?.isSyncing || state?.offline?.syncGeneration !== generation
      ? state?.auditReports?.allReportsData?.[reportId] || null : null
  }
}
